<template>
  <!-- Page Content -->
  <div class="no_nav_height">
    <div class="row f-row justify-content-center h-100">
      <!-- Row: title -->
      <div class="row f-row mb-1 markets-chart-title d-flex align-items-center">
        <div class="col-5 d-flex align-items-center" style="font-size: 1rem">
          Fundamentals
        </div>
        <span
          style="color: var(--textactive); font-size: 1rem"
          class="col col-4"
        >
          {{ stringTitle }}</span
        >
        <slot name="spot"></slot>
      </div>
      <!-- Row: controls and labels -->
      <div
        class="controls-labels row f-row txt-label-primary-2 w-100 justify-content-start"
      >
        <span v-if="loading.fundamentalOptions" class="text-center w-100">
          <ProgressSpinner :size="'20px'" />
        </span>

        <template v-else>
          <!-- Fundamentals -->
          <div class="col col-auto txt-label-primary-2 text-left">
            <span class="mr-2">Fundamentals:</span>
          </div>
          <div class="col col-2 text-left" style="z-index: 1000!important;">
            <MultiSelect
              v-model="fundamentalOptions"
              :options="allData.fundamentalOptions"
              :filter="true"
              :selectionLimit="2"
              placeholder="Select Fundamentals"
              class="p-column-filter"
              optionLabel="name"
              scrollHeight="400px"
            >
              <template #option="slotProps">
                <div class="p-multiselect-representative-option">
                  <span class="image-text">{{ slotProps.option.name }}</span>
                </div>
              </template>
            </MultiSelect>
          </div>
          <!-- Commodities type -->
          <div class="col col-auto txt-label-primary-2 text-center">
            <span class="ml-3 mr-2">Type:</span>
          </div>
          <div class="col col-2 text-left" style="z-index: 1000!important;">
            <MultiSelect
              v-model="assetTypes"
              :options="allData.assetTypesCopy"
              :filter="true"
              placeholder="Select Asset Types"
              class="p-column-filter"
              optionLabel="name"
              scrollHeight="400px"
            >
              <template #option="slotProps">
                <div class="p-multiselect-representative-option">
                  <span class="image-text">{{ slotProps.option.name }}</span>
                </div>
              </template>
            </MultiSelect>
          </div>
          <!-- Commodities -->
          <div class="col col-auto txt-label-primary-2 text-center">
            <span class="ml-3 mr-2">Commodities:</span>
          </div>
          <div class="col col-2 text-left" style="z-index: 1000!important;">
            <MultiSelect
              v-model="assets"
              :options="allData.assetOptionsCopy"
              :filter="true"
              placeholder="Select Assets"
              class="p-column-filter"
              optionLabel="name"
              scrollHeight="400px"
            >
              <template #option="slotProps">
                <div class="p-multiselect-representative-option">
                  <span class="image-text">{{ slotProps.option.name }}</span>
                </div>
              </template>
            </MultiSelect>
          </div>
          <div class="col col-1"></div>
          <div class="col col-2">
            <button
              type="button"
              :disabled="!canFetchData"
              class="btn btn-strat-blue"
              style="margin: 0!important;"
              @click="onUserFetchData"
            >
              <span v-if="loading.fundamentals" class="text-left mr-2">
                <QSLoading
                  :isLoading="showLoadingSpot"
                  :size="20"
                  :colorTheme="colorTheme"
                />
              </span>
              <span v-if="loading.fundamentals">Loading Data</span>
              <span v-else>Fetch Data</span>
            </button>
          </div>
        </template>
      </div>
      <!-- Chart -->
      <div class="flex-center-column w-100 position-relative container-chart">
        <!-- Row: legend -->
        <div
          class="row f-row container-legends"
          v-if="allData.fundamentals && allData.fundamentals.length > 0"
        >
          <div class="col col-auto text-left" style="z-index: 1;">
            <table
              class="table table-sm table-responsive table-borderless table-market-legend"
              style="width: 100%; background: rgba(0,0,0,0)"
            >
              <tbody>
                <tr>
                  <td
                    class="text-left"
                    v-for="(year, i) in formatVars.years"
                    :key="i"
                  >
                    <div
                      class="square-legends"
                      :style="{
                        backgroundColor: formatVars.colorSelection[i],
                        borderColor: formatVars.colorSelection[i],
                      }"
                    ></div>
                    {{ year }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- Row Chart -->
        <div class="row f-row h-100">
          <!-- Skeleton chart -->
          <div
            v-if="loading.fundamentals || !allData.fundamentals"
            class="text-center"
            style="width: 100%; height: 100%;"
          >
            <chart-bar
              :processData="processData"
              :colorTheme="colorTheme"
              :hasButton="false"
            />
          </div>
          <!-- No data available -->
          <div
            v-else-if="allData.fundamentals.length === 0"
            class="text-center"
            style="width: 100%; top: calc(50% - 8px)!important; position: absolute;"
          >
            No Data Available
          </div>
          <div class="col col-12" style="height:100%; width:100%">
            <div
              class="h-100 d-flex flex-column"
              id="fundamentals-div"
              style="width: 100%; "
            >
              <svg id="svg-fundamentals"></svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from "@/_core/refactor/Chart";
import {
  colorExpo,
  colorSelection3Alpha,
  currencyFormat,
} from "@/_core/_helpers/_parametric_tools";
import { fundamentalsHover } from "@/_core/charts/Fundamentals";
import {
  getFundamentals,
  postFundamentals,
} from "@/_core/data/api_calls/getFundamentals";
import { select, min, nest } from "d3";
import ChartBar from "../../skeletons/ChartBar.vue";
import MultiSelect from "primevue/multiselect";
import ProgressSpinner from "../../ProgressSpinner.vue";
import QSLoading from "@/components/QSLoading.vue";

export default {
  components: { ChartBar, MultiSelect, ProgressSpinner, QSLoading },
  props: {
    commoditySpecs: { required: true },
    processData: { required: true },
    colorTheme: { required: true, type: Boolean, default: true },
  },
  data() {
    return {
      allData: {
        fundamentalOptions: null,
        assetTypes: null,
        assetTypesCopy: null,
        assetOptions: null,
        assetOptionsCopy: null,
        dataRaw: null,
        fundamentals: null,
        nestedData: null,
        render: false,
      },
      chartElements: {
        D3Chart: null,
        svgNode: null,
        chartBody: null,
      },
      loading: {
        fundamentalOptions: false,
        fundamentals: false,
      },
      formatVars: {
        assets: "",
        assetTypes: "",
        currencyFormat: currencyFormat,
        fundamentalOptions: "",
        logo: require("@/assets/images/symbol_blue.svg"),
        fieldColorBars: colorExpo,
        colorSelection: colorSelection3Alpha,
        years: [],
      },
      userSelections: {},
      svgNode: null,
    };
  },
  created() {
    this.getDataOptions();
    // ===== create initial charts instances =====
    this.chartElements.D3Chart = new Chart("fundamentals");
  },
  mounted() {
    this.updateFormatVars();
  },
  computed: {
    stringTitle() {
      const fundamentals =
        this.fundamentalOptions.length > 1
          ? `${this.fundamentalOptions.length}  fundamentals`
          : `${this.fundamentalOptions[0]?.name ?? "N.A"}`;

      const assetTypes =
        this.assetTypes.length > 1
          ? `${this.assetTypes.length} types`
          : `${this.assetTypes[0]?.name ?? "N.A"}`;

      const commodities =
        this.assets.length > 1
          ? `${this.assets.length} assets`
          : `${this.assets[0]?.name ?? "N.A"}`;

      return `${fundamentals} | ${assetTypes} | ${commodities}`;
    },
    assetTypes: {
      get: function() {
        return this.formatVars.assetTypes;
      },
      set: function(newValue) {
        this.onUserAssetType(newValue);
      },
    },
    assets: {
      get: function() {
        return this.formatVars.assets;
      },
      set: function(newValue) {
        this.onUserAsset(newValue);
      },
    },
    fundamentalOptions: {
      get: function() {
        return this.formatVars.fundamentalOptions;
      },
      set: function(newValue) {
        this.onUserFundamentalOption(newValue);
      },
    },
    defaultAssetType() {
      return [this.allData.assetTypes[0]] || [];
    },
    defaultAsset() {
      return this.filterAssetsByAssetTypes(this.defaultAssetType) || [];
    },
    defaultFundamentalOption() {
      return [this.allData.fundamentalOptions[0]] || [];
    },
    canFetchData() {
      const { assetTypes, assets, fundamentalOptions } = this.formatVars;
      return assetTypes && assets && fundamentalOptions;
    },
  },
  watch: {
    "allData.fundamentalOptions": {
      handler() {
        this.updateFormatVars();
      },
    },
    "loading.fundamentals": {
      handler(val) {
        if (!val) {
          this.chartRendering();
        }
      },
      deep: true,
    },
    colorTheme() {
      this.chartRendering();
    },
    "formatVars.fundamentalOptions": {
      handler() {
        this.filterAssetsByFundamentals();
      },
    },
    "allData.assetOptions": {
      handler(val) {
        this.allData.assetOptionsCopy = val;
      },
    },
    "allData.assetTypes": {
      handler(val) {
        if (val) {
          this.allData.assetTypesCopy = val;
        }
      },
    },
  },
  methods: {
    /* Configuration chart */
    chartRendering() {
      if (this.allData["fundamentals"].length > 0) {
        this.createChart();
      } else this.deleteChart();
    },
    createChart() {
      if (this.allData["fundamentals"].length === 0) {
        return;
      }
      // === set SVG canvas
      this.chartElements.D3Chart.setSVG({
        top: { perc: 0.1, px: 10 },
        right: { perc: 0.2, px: 30 },
        bottom: { perc: 0.5, px: 30 },
        left: { perc: 0.25, px: 75 },
        colorTheme: this.colorTheme,
        logo: this.formatVars["logo"],
      });

      // === minMax
      let minMaxYAxis;
      minMaxYAxis = this.getMinMaxYAxis();
      minMaxYAxis = [minMaxYAxis[0], minMaxYAxis[1]];

      this.chartElements.svgNode = this.chartElements.D3Chart.svg;
      this.svgNode = this.chartElements.D3Chart.svg;
      // Get unique years
      this.formatVars.years = [
        ...new Set(
          this.allData.nestedData.flatMap((d) =>
            Object.keys(d).filter((key) => key !== "name"),
          ),
        ),
      ];
      // === set axis
      this.chartElements.D3Chart.setAxis({
        xData: this.allData.nestedData,
        valueScaleBand: "name",
        minMaxYAxis: minMaxYAxis,
        xFormat: "",
        svgX1: true,
        keys: this.formatVars.years,
        colorScale: colorSelection3Alpha, // []
      });

      // === min max range area
      const bandWidth = this.chartElements["D3Chart"].svg.x.bandwidth();
      const candleWidth = (barWidth) =>
        Math.floor(min([bandWidth * barWidth, 1000000]) / 2) * 2 + 1;
      // === add bars
      this.chartElements["D3Chart"].addBars({
        type: "bar",
        data: this.allData.nestedData,
        customName: "fund",
        yField: "value",
        fieldColor: this.formatVars.fieldColorBars(this.colorTheme),
        candleWidth: candleWidth(0.5),
        lineWidth: 3,
        xIsDate: false,
        date: "name",
        height: 1.5,
        svgX1: true,
        keys: this.formatVars.years,
      });

      // === line of second fundamental
      if (this.formatVars.fundamentalOptions.length > 1) {
        this.chartElements["D3Chart"].addLines({
          type: "line",
          data: this.allData.fundamentals,
          color: "yellow",
          lineWidth: 2,
          xIsDate: false,
          fieldLine: "value",
          date: "commodityName",
        });
      }

      // Hovers
      // lines
      this.chartElements["D3Chart"].addHovers({
        colorTheme: this.colorTheme,
        hLine: true,
        vLine: true,
      });
      // Tooltips
      this.chartElements["D3Chart"].createToolTipsHover(["y", "x"], {
        currencyFormat: this.formatVars.currencyFormat,
        colorTheme: this.colorTheme,
      });

      this.chartElements.svgNode = this.chartElements.D3Chart.svg;
      this.svgNode = this.chartElements.D3Chart.svg;
      // Add hover function
      fundamentalsHover(this, this.chartElements["D3Chart"]);

      this.chartElements.chartBody = this.chartElements.D3Chart.svgAux;
    },
    getMinMaxYAxis() {
      let minMaxYAxis = [0, 1];

      // Create data min max
      const dataMinMax = this.allData.nestedData.map((item) => {
        const values = Object.keys(item)
          .filter((key) => !Number.isNaN(key))
          .map((key) => item[key]);

        return {
          min: Math.min(...values),
          max: Math.max(...values),
        };
      });

      minMaxYAxis = this.processData.minMax(dataMinMax, "max", "min");

      return [minMaxYAxis[0], minMaxYAxis[1]];
    },
    deleteChart() {
      select("#fundamentals-div")
        .selectAll(".tooltip")
        .remove();
      select("#fundamentals-div")
        .select("svg > *")
        .remove();
    },
    /* Events */
    onUserAssetType(val) {
      this.formatVars.assetTypes = val;
      this.onUserAsset(val, true);
    },
    onUserAsset(val, isType = false) {
      let filterAssets = val;
      // Filter all assets that relationship whit commodity type
      if (isType) {
        filterAssets = this.filterAssetsByAssetTypes(val);
      }
      // Filter asset types by current selected commodities
      this.filterAssetTypesByAssetsSelected(filterAssets);
      this.formatVars.assets = filterAssets;
    },
    onUserFundamentalOption(val) {
      Object.assign(this.formatVars, {
        fundamentalOptions: val,
      });
    },
    /* Configuration data */
    updateFormatVars() {
      this.formatVars.assets = this.defaultAsset;
      this.formatVars.assetTypes = this.defaultAssetType;
      this.formatVars.fundamentalOptions = this.defaultFundamentalOption;
    },
    filterAssetsByAssetTypes(data) {
      return this.allData.assetOptionsCopy?.filter((d) =>
        data.find((v) => v?.fk_idcommoditytype === d.fk_idcommoditytype),
      );
    },
    filterAssetsByFundamentals() {
      this.allData.assetOptionsCopy = this.allData.assetOptions?.filter(
        (asset) =>
          this.formatVars.fundamentalOptions.some((d) =>
            asset.fk_fundamentals.includes(d.pk_fundamental),
          ),
      );

      this.allData.assetTypesCopy = this.allData.assetTypes?.filter(
        (assetType) =>
          this.allData.assetOptionsCopy.find(
            (d) => d.fk_idcommoditytype === assetType.fk_idcommoditytype,
          ),
      );
      this.filterAssetTypesByAssetsSelected(this.allData.assetOptionsCopy);
      this.formatVars.assetTypes = [this.formatVars.assetTypes[0]];

      this.formatVars.assets = this.filterAssetsByAssetTypes(
        this.formatVars.assetTypes,
      );
    },
    filterAssetTypesByAssetsSelected(data) {
      this.formatVars.assetTypes = this.allData.assetTypes.filter((assetType) =>
        data.find((d) => d.fk_idcommoditytype === assetType.fk_idcommoditytype),
      );
    },
    nestedData(data) {
      const nestedData = nest()
        // Group by commodity name
        .key((d) => d.commodityName)
        .entries(data)
        .map((group) => ({
          name: group.key,
          ...group.values.reduce((acc, item) => {
            const year = item.observation_date.split("-")[0];
            acc[year] = (acc[year] || 0) + item.value;
            return acc;
          }, {}),
        }));

      Object.assign(this.allData, {
        nestedData,
      });
    },
    onUserFetchData() {
      this.deleteChart();
      Object.assign(this.allData, { fundamentals: [] });
      Object.assign(this.allData, { render: true });

      const assetIds = this.formatVars.assets.map((d) => d.pk_commodity);
      const fundamentalIds = this.formatVars.fundamentalOptions.map(
        (d) => d.pk_fundamental,
      );
      const queryParams = {
        assets: assetIds,
        fk_fundamentals: fundamentalIds,
        periodicity: "W",
        date: "2020-01-01",
      };

      postFundamentals(this, queryParams);
    },
    getDataOptions() {
      this.deleteChart();
      Object.assign(this.allData, { fundamentalOptions: [] });
      Object.assign(this.allData, { assetTypes: [] });
      Object.assign(this.allData, { assetOptions: [] });
      Object.assign(this.allData, { fundamentals: null });
      getFundamentals(this);
    },
  },
};
</script>

<style scoped>
.container-legends {
  top: 30px;
  position: absolute;
  left: 90px;
}
.container-chart {
  height: 90%;
  max-height: 90%;
}
</style>
